/**
 * WeCreat Countdown Component (Single File Version)
 * Usage: <wc-countdown deadline="2026-12-31T23:59:59" timezone="America/Los_Angeles"></wc-countdown>
 */

// 1. 定义 HTML 模板和 CSS 样式
const TEMPLATE_HTML = `
<style>
  :host {
    display: inline-block;
    --bg-color: var(--we-bg-color, transparent);
    --box-bg: var(--we-box-bg, #ffffff);
    --box-size: var(--we-box-size, 32px);
    --box-text: var(--we-box-text, #333333);
    --box-radius: var(--we-box-radius, 8px);
    --label-color: var(--we-label-color, #ffffff);
    --label-display: var(--we-label-display, block);
    --font-size: var(--we-font-size, 16px);
    --gap: var(--we-gap, 8px);
    --m-box-size: var(--we-m-box-size, 26px);
    --m-gap: var(--we-m-gap, 4px);
    --m-font-size: var(--we-m-font-size, 14px);
    --m-box-radius: var(--we-m-box-radius, 6px);
  }
  .container {
    background-color: var(--bg-color);
    text-align: center;
    font-family: var(--font-body-family, sans-serif);
    font-family:Lato, sans-serif;
  }
  .timer-wrapper {
    display: flex;
    justify-content: center;
    gap: var(--gap);
  }
  .block {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .box {
    background-color: var(--box-bg);
    color: var(--box-text);
    font-size: var(--font-size);
    font-weight: 700;
    border-radius: var(--box-radius);
    width:var(--box-size);
    height:var(--box-size);
    line-height: var(--box-size);
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  .label {
    font-size: 12px;
    color: var(--label-color);
    text-transform: uppercase;
    line-height: 16px;
    display:var(--label-display)
  }
  .colon {
    font-size: 16px;
    color: var(--label-color);
    font-weight: 700;
    line-height: var(--box-size);
    // margin-bottom: 16px; 
  }
  .expiry-message {
    display: none;
    color: var(--label-color);
    font-size: calc(var(--font-size) * 0.8);
    font-weight: bold;
    padding: 10px;
  }
  @media(max-width:768px){
    .timer-wrapper {
        gap: var(--m-gap);
    }
    .box {
        width:var(--m-box-size);
        height:var(--m-box-size);
        line-height: var(--m-box-size);
        font-size: var(--m-font-size);
        border-radius: var(--m-box-radius);
    }
    .colon {
        line-height: var(--m-box-size); 
    }
  }
</style>

<div class="container">
  <div class="timer-wrapper">
    <div class="block">
      <div class="box" data-unit="days">00</div>
      <div class="label">Days</div>
    </div>
    <div class="colon">:</div>
    <div class="block">
      <div class="box" data-unit="hours">00</div>
      <div class="label">Hrs</div>
    </div>
    <div class="colon">:</div>
    
    <div class="block">
      <div class="box" data-unit="minutes">00</div>
      <div class="label">Min</div>
    </div>
    <div class="colon">:</div>
    
    <div class="block">
      <div class="box" data-unit="seconds">00</div>
      <div class="label">Sec</div>
    </div>
  </div>
  
  <div class="expiry-message"></div>
</div>
`;

// 2. 定义 Web Component 类
if (!customElements.get("we-countdown")) {
  class WcCountdown extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
      // 注入模板
      this.shadowRoot.innerHTML = TEMPLATE_HTML;

      // 获取参数
      this.deadlineISO = this.getAttribute("deadline").trim().replace(' ', 'T').replace(/\//g, '-');
      this.timeZone = this.getAttribute("timezone") === 'system_time' ? Intl.DateTimeFormat().resolvedOptions().timeZone : (this.getAttribute("timezone") || "Asia/Shanghai");
      this.expiryText = this.getAttribute("expiry-text") || "EXPIRED";

      // 绑定 UI 元素
      this.ui = {
        days: this.shadowRoot.querySelector('[data-unit="days"]'),
        hours: this.shadowRoot.querySelector('[data-unit="hours"]'),
        minutes: this.shadowRoot.querySelector('[data-unit="minutes"]'),
        seconds: this.shadowRoot.querySelector('[data-unit="seconds"]'),
        timer: this.shadowRoot.querySelector(".timer-wrapper"),
        message: this.shadowRoot.querySelector(".expiry-message"),
      };

      this.ui.message.textContent = this.expiryText;

      if (this.deadlineISO) this.startTimer();
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    startTimer() {
      const update = () => {
        // 获取当前时区的时间
        const now = new Date();
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: this.timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).formatToParts(now);

        const mapping = {};
        parts.forEach(({ type, value }) => (mapping[type] = value));
        const targetNowStr = `${mapping.year}-${mapping.month}-${mapping.day}T${mapping.hour}:${mapping.minute}:${mapping.second}`;

        const distance = new Date(this.deadlineISO) - new Date(targetNowStr);

        if (distance < 0) {
          this.handleExpiry();
          return;
        }

        const d = Math.floor(distance / (1000 * 60 * 60 * 24));
        const h = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const m = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((distance % (1000 * 60)) / 1000);

        this.updateUI(d, h, m, s);
      };

      update();
      this.interval = setInterval(update, 1000);
    }

    updateUI(d, h, m, s) {
      if (this.ui.days.textContent != d) this.ui.days.textContent = this.fmt(d);
      if (this.ui.hours.textContent != h)
        this.ui.hours.textContent = this.fmt(h);
      if (this.ui.minutes.textContent != m)
        this.ui.minutes.textContent = this.fmt(m);
      this.ui.seconds.textContent = this.fmt(s);
    }

    handleExpiry() {
      clearInterval(this.interval);
    //   this.ui.timer.style.display = "none";
    //   this.ui.message.style.display = "block";
    }

    fmt(n) {
      return n < 10 ? `0${n}` : n;
    }
  }

  customElements.define("we-countdown", WcCountdown);
}
